import { useTranslation } from 'react-i18next';

import type { TimeRange } from '@/shared/charts';

import type { TimeSeriesChartModel } from '../../types/TimeSeriesChartModel.types';
import type { TimeSeriesChartStatus } from '../../types/TimeSeriesChartStatus.types';
import { TimeSeriesChartEmptyState } from '../time-series-chart-empty-state/TimeSeriesChartEmptyState';
import { TimeSeriesChartErrorState } from '../time-series-chart-error-state/TimeSeriesChartErrorState';
import { TimeSeriesChartLoadingState } from '../time-series-chart-loading-state/TimeSeriesChartLoadingState';
import { TimeSeriesChartView } from '../time-series-chart-view/TimeSeriesChartView';

export type TimeSeriesChartResultPanelProps = {
  chart: TimeSeriesChartModel;
  timeDomain: TimeRange | undefined;
  /** The line series shown as the navigation slider's shadow preview; see TimeSeriesChartNavigation. */
  previewSeriesId: string | undefined;
  status: TimeSeriesChartStatus;
  onRetry: () => void;
};

/** The result area for one chart status; see TimeSeriesChartStatus. */
export function TimeSeriesChartResultPanel({
  chart,
  timeDomain,
  previewSeriesId,
  status,
  onRetry,
}: TimeSeriesChartResultPanelProps) {
  const { t } = useTranslation();

  // The window comes from the URL, the status from the query; both must be there to draw a chart.
  if (!timeDomain) {
    return <TimeSeriesChartEmptyState />;
  }

  switch (status.type) {
    case 'empty':
      return <TimeSeriesChartEmptyState />;

    case 'loading':
      return <TimeSeriesChartLoadingState />;

    case 'error':
      return (
        <TimeSeriesChartErrorState
          message={t('timeSeriesChart.chart.error')}
          isRetrying={status.isRetrying}
          onRetry={onRetry}
          data-qa-element-id="time-series-chart-error"
        />
      );

    case 'ready':
      return (
        <TimeSeriesChartView
          chart={chart}
          timeDomain={timeDomain}
          previewSeriesId={previewSeriesId}
          isRefreshing={status.isRefreshing}
          hasRefreshError={status.hasRefreshError}
          onRetry={onRetry}
        />
      );

    default:
      return status satisfies never;
  }
}
